import { useEffect, useState } from "react"
import { X } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "./AuthProvider"
import { supabase } from "../lib/supabase"
import { ROUTES } from "../lib/routes"

const SPOTLIGHT_KEY = "luster_spotlight_shown"
const AUTO_DISMISS_MS = 8000

type GoalConfig = {
  headline: string
  desc: string
  route: string
  routeLabel: string
  features: string[]
}

const GOAL_MAP: Record<string, GoalConfig> = {
  operations: {
    headline: "Start with Protocols",
    desc: "Create your first protocol checklist and capture photos from any facility.",
    route: ROUTES.adminProtocols,
    routeLabel: "Open Protocols →",
    features: ["Protocol templates", "Photo capture", "Crew scheduling", "GPS clock-in"],
  },
  performance: {
    headline: "Check your Reports",
    desc: "View clean scores, trend charts, and export data for your clients.",
    route: ROUTES.activities,
    routeLabel: "Open Reports →",
    features: ["Clean report scores", "Trend charts", "PDF export", "Signal feed"],
  },
  incidents: {
    headline: "Open Platform Trace",
    desc: "Log your first incident and see how Trace tracks service quality.",
    route: ROUTES.traceFeed,
    routeLabel: "Open Platform Trace →",
    features: ["Incident logging", "SEV levels", "Escalation flow", "Public trace page"],
  },
  clients: {
    headline: "Add your Clients",
    desc: "Set up clients and properties to start tracking facility activity.",
    route: "/admin/users?tab=clients",
    routeLabel: "Open Clients →",
    features: ["Client pipeline", "Property setup", "Onboarding flow", "Billing tracking"],
  },
  explore: {
    headline: "Start at the Dashboard",
    desc: "Get an overview of all modules — Manifold, Pulse, Cortex, and more.",
    route: ROUTES.home,
    routeLabel: "Open Dashboard →",
    features: ["Manifold overview", "Property Pulse", "Luster Amplitude", "Cortex AI"],
  },
}

export function OnboardingSpotlight() {
  const { companyId, role } = useAuth()
  const navigate = useNavigate()
  const [goal, setGoal] = useState<string | null>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (role !== "admin" || !companyId) return
    if (localStorage.getItem(SPOTLIGHT_KEY)) return

    supabase
      .from("companies")
      .select("onboarding_goal")
      .eq("id", companyId)
      .single()
      .then(({ data }) => {
        if (!data?.onboarding_goal) return
        setGoal(data.onboarding_goal)
        setVisible(true)
      })
  }, [companyId, role])

  useEffect(() => {
    if (!visible) return
    const t = setTimeout(() => dismiss(), AUTO_DISMISS_MS)
    return () => clearTimeout(t)
  }, [visible])

  const dismiss = () => {
    setVisible(false)
    localStorage.setItem(SPOTLIGHT_KEY, "1")
  }

  const handleNavigate = (route: string) => {
    dismiss()
    navigate(route)
  }

  if (!visible || !goal) return null
  const config = GOAL_MAP[goal] ?? GOAL_MAP.explore

  return (
    <div className="fixed bottom-6 right-6 z-[80] w-72 animate-in slide-in-from-bottom-4 fade-in duration-300">
      <div className="rounded-2xl bg-black/70 backdrop-blur-2xl border border-white/[0.10] shadow-[0_24px_60px_-12px_rgba(0,0,0,0.7)] p-5 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-0.5">
            <p className="font-mono text-[9px] text-teal-400/70 uppercase tracking-widest">ONBOARDING.SPOTLIGHT</p>
            <h3 className="font-semibold text-white text-sm">{config.headline}</h3>
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="shrink-0 w-6 h-6 rounded-lg flex items-center justify-center text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        <p className="text-white/50 text-xs leading-relaxed">{config.desc}</p>

        {/* Feature pills */}
        <div className="flex flex-wrap gap-1.5">
          {config.features.map((f) => (
            <span
              key={f}
              className="px-2 py-0.5 rounded-full bg-white/[0.06] border border-white/[0.08] font-mono text-[9px] text-white/40 uppercase tracking-wider"
            >
              {f}
            </span>
          ))}
        </div>

        {/* CTA */}
        <button
          type="button"
          onClick={() => handleNavigate(config.route)}
          className="w-full h-9 rounded-xl bg-teal-500/10 border border-teal-400/20 text-teal-300 hover:bg-teal-500/20 font-mono text-[10px] font-bold uppercase tracking-widest transition-colors"
        >
          {config.routeLabel}
        </button>

        {/* Auto-dismiss hint */}
        <p className="font-mono text-[9px] text-white/20 text-center">Dismisses automatically in {AUTO_DISMISS_MS / 1000}s</p>
      </div>
    </div>
  )
}
